import { Config } from '@config'
import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
  type Messaging,
} from 'firebase/messaging'

let cachedApp: FirebaseApp | null = null
let cachedMessaging: Messaging | null = null

interface WebFcmConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

function readWebConfig(): WebFcmConfig | null {
  const cfg = {
    apiKey: Config.FIREBASE_API_KEY,
    authDomain: Config.FIREBASE_AUTH_DOMAIN,
    projectId: Config.FIREBASE_PROJECT_ID,
    storageBucket: Config.FIREBASE_STORAGE_BUCKET,
    messagingSenderId: Config.FIREBASE_MESSAGING_SENDER_ID,
    appId: Config.FIREBASE_APP_ID,
  }
  if (
    !cfg.apiKey ||
    !cfg.authDomain ||
    !cfg.projectId ||
    !cfg.messagingSenderId ||
    !cfg.appId
  ) {
    return null
  }
  return cfg as WebFcmConfig
}

function isMessagingSupportedInBrowser(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  )
}

async function ensureMessaging(): Promise<Messaging | null> {
  if (cachedMessaging) return cachedMessaging

  // Firebase's own async `isSupported()` is the source of truth. The previous
  // manual heuristic could pass while Firebase still refused to initialise
  // messaging — `getMessaging()` then threw "Service messaging is not
  // available", spamming Sentry AND leaving web push silently non-functional.
  // Only touch `getMessaging()` once Firebase itself has confirmed support.
  let supported = false
  try {
    supported = await isSupported()
  } catch {
    supported = false
  }
  if (!supported) return null

  const cfg = readWebConfig()
  if (!cfg) return null

  try {
    if (!cachedApp) {
      cachedApp = initializeApp(cfg)
    }
    cachedMessaging = getMessaging(cachedApp)
    return cachedMessaging
  } catch {
    // Belt-and-suspenders: never let a Firebase init failure bubble up.
    return null
  }
}

/**
 * Ensure the FCM service worker is registered. The file lives at
 * `/firebase-messaging-sw.js` (served from `public/`).
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    const existing = await navigator.serviceWorker.getRegistration(
      '/firebase-messaging-sw.js',
    )
    if (existing) return existing
    return await navigator.serviceWorker.register('/firebase-messaging-sw.js')
  } catch {
    return null
  }
}

/**
 * Request notification permission from the browser. Returns true if
 * granted; false if denied or unavailable.
 */
export async function requestWebPermission(): Promise<boolean> {
  if (!isMessagingSupportedInBrowser()) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  const result = await Notification.requestPermission()
  return result === 'granted'
}

/**
 * Fetch the FCM Web Push token for this browser. Returns null if FCM
 * is not configured (missing env), unsupported, or permission denied.
 */
export async function getWebFcmToken(): Promise<string | null> {
  const messaging = await ensureMessaging()
  if (!messaging) return null
  if (!Config.FIREBASE_VAPID_KEY) return null

  const granted = await requestWebPermission()
  if (!granted) return null

  const swReg = await registerServiceWorker()
  if (!swReg) return null

  try {
    const token = await getToken(messaging, {
      vapidKey: Config.FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swReg,
    })
    return token || null
  } catch {
    return null
  }
}

export type WebForegroundHandler = (payload: MessagePayload) => void

/**
 * Subscribe to foreground messages. Returns an unsubscribe function.
 */
export function onWebForegroundMessage(handler: WebForegroundHandler): () => void {
  // ensureMessaging() is now async. Keep the synchronous unsubscribe contract:
  // subscribe once messaging resolves, and no-op the unsubscribe if the caller
  // cleaned up before then (or messaging is unsupported).
  let unsubscribe: () => void = () => undefined
  let cancelled = false

  void ensureMessaging()
    .then((messaging) => {
      if (cancelled || !messaging) return
      unsubscribe = onMessage(messaging, handler)
    })
    .catch(() => undefined)

  return () => {
    cancelled = true
    unsubscribe()
  }
}
